import { isShapeType } from "../../../util/shapeTypeMapper";
import { flushSync } from "react-dom";
import { isPrimaryMouseButton } from "./pointerButtonUtils";

/**
 * Creates a click handler for canvas element selection
 * @param {Object} params - Parameters object
 */
export const createOnClick = ({
  stageRef,
  offCanvasStageRef = null,
  topLayerStageRef = null,
  /** @type {React.MutableRefObject<{ timeStamp?: number; targetId?: string } | null> | null} */
  multiSelectCanvasClickDedupeRef = null,
  setSelectedImage,
  setSelectedLine,
  setSelectedArrow, // Add setSelectedArrow to clear arrow selections
  setSelectedDoubleSidedArrow, // Add setSelectedDoubleSidedArrow to clear double-sided arrow selections
  isElementBeingEdited,
  endElementEditing,
  setSelectedObjects,
  setSelectedObject,
  selectedObjects,
  selectedObjectsRef = null,
  selectedObject,
  setSelectedText,
  setShowTextBox,
  setSelectedTextId,
  setTexts,
  texts, // CRITICAL: Pass texts array to read selectedContent synchronously
  offCanvasTexts = [], // Off-canvas text state (actualElement lookup; same as main texts)
  isDrawingArrow,
  isDrawingDoubleArrow,
  isDrawingLine,
  setAction,
  action,
  rectangles,
  offCanvasRectangles,
  circles,
  offCanvasCircles,
  images,
  offCanvasImages,
  videos,
  offCanvasVideos,
  arrows,
  offCanvasArrows,
  lines,
  offCanvasLines,
  doubleSidedArrows,
  offCanvasDoubleSidedArrows,
  ACTIONS_SELECT,
  lockedAction,
  isCloneMode = false, // Clone mode flag (additional check for clone mode)
  justFinishedSelectionRef, // Ref to check if we just finished a selection box drag
  pendingChangesRef = null, // Only call endElementEditing when true so selection effect clears on first click
  isMultiSelectMode = false, // Multi-select mode flag
  isMultiSelectModeRef = null, // Ref to avoid stale closures
  setIsMultiSelectMode, // Function to update multi-select mode
  preMouseDownSelectionCountRef = null, // Ref to capture selection count before Stage.onMouseDown clears it
  isCropModalOpen = false, // When crop modal is open, don't clear selection (modal interaction must not close it)
  isCropModalOpenRef = null, // Ref set synchronously on Crop click so first-tick events see "open" (fixes first-time close)
}) => {
  return function onClick(e) {
    if (!isPrimaryMouseButton(e.evt)) {
      return;
    }

    // CRITICAL: Check for clone mode FIRST, before any clearing operations
    // If a tool is locked (clone mode) or clone mode is active, don't interfere with clone creation
    // Return early to let handleStageMouseDown manage clone creation
    // This prevents selection changes when clicking in clone mode
    if (lockedAction || isCloneMode) {
      e.evt?.preventDefault?.();
      e.evt?.stopPropagation?.();
      return;
    }

    // When a drawing tool is active, pointer-up handlers own creation + selection.
    // Ignore stage click clear logic so click-to-create selections are not immediately cleared.
    const isNonSelectToolActive = action && action !== ACTIONS_SELECT;
    if (isNonSelectToolActive) {
      e.evt?.preventDefault?.();
      e.evt?.stopPropagation?.();
      return;
    }

    // CRITICAL: When crop modal is open (state or ref), don't clear selection on stage click.
    // Ref is set synchronously when Crop is clicked so first interaction never sees stale state.
    if (isCropModalOpen || isCropModalOpenRef?.current) {
      e.evt?.preventDefault?.();
      e.evt?.stopPropagation?.();
      return;
    }

    if (!stageRef.current) {
      return;
    }

    const targetIdForPointer = e?.target?.attrs?.id;
    const isIdOnOffCanvas =
      targetIdForPointer != null &&
      (offCanvasTexts.some((t) => t.id === targetIdForPointer) ||
        offCanvasRectangles.some((r) => r.id === targetIdForPointer) ||
        offCanvasCircles.some((c) => c.id === targetIdForPointer) ||
        offCanvasImages.some((i) => i.id === targetIdForPointer) ||
        offCanvasVideos.some((v) => v.id === targetIdForPointer) ||
        offCanvasArrows.some((a) => a.id === targetIdForPointer) ||
        offCanvasDoubleSidedArrows.some((d) => d.id === targetIdForPointer) ||
        offCanvasLines.some((l) => l.id === targetIdForPointer));

    const stage =
      isIdOnOffCanvas && offCanvasStageRef?.current
        ? offCanvasStageRef.current
        : stageRef.current;

    // HTML / delegated clicks do not update Konva pointer unless we sync from the native event
    if (e?.evt && typeof stage.setPointersPositions === "function") {
      stage.setPointersPositions(e.evt);
    }

    const pointerPosition = stage.getPointerPosition();

    // Add null check for pointerPosition
    if (!pointerPosition) {
      return;
    }

    const scale = stage.scaleX();
    const x = pointerPosition.x / scale;
    const y = pointerPosition.y / scale;

    const clampedX = Math.max(10, Math.min(x, stage.width() / scale - 10));
    const clampedY = Math.max(10, Math.min(y, stage.height() / scale - 10));

    const target = e.target;
    const targetId = target?.attrs?.id;

    // Use ref to get current value and avoid stale closures
    const currentIsMultiSelectMode = isMultiSelectModeRef?.current ?? isMultiSelectMode;

    // Clear single selections (only if not in clone mode - checked above)
    setSelectedImage(null);
    setSelectedLine(null);
    // CRITICAL: Also clear arrow selections (unified arrow elements might be stored as selectedArrow or selectedDoubleSidedArrow)
    if (setSelectedArrow) setSelectedArrow(null);
    if (setSelectedDoubleSidedArrow) setSelectedDoubleSidedArrow(null);
    // CRITICAL: For Text elements, target.getClassName() returns "Group", but target.attrs.type is "Text"
    // For UnifiedLineArrowElement, target.getClassName() returns "Arrow" (Konva class), but the element
    // might be stored in lines array. Check attrs.type first, then fall back to getClassName()
    let type = target?.attrs?.type || target.getClassName();

    // CRITICAL: Check if this is a unified arrow element in the lines array
    // This is needed because arrows are stored in the lines array but should be identified as "Arrow" or "DoubleSidedArrow"
    const unifiedArrow = lines.find((l) => {
      if (l.id !== targetId) return false;
      const hasStartArrow =
        l.hasStartArrow === true || l.pointerAtBeginning === true;
      const hasEndArrow = l.hasEndArrow === true || l.pointerAtEnd === true;
      return hasStartArrow || hasEndArrow;
    });

    if (unifiedArrow) {
      // Determine the correct type based on arrow heads
      const hasStartArrow =
        unifiedArrow.hasStartArrow === true ||
        unifiedArrow.pointerAtBeginning === true;
      const hasEndArrow =
        unifiedArrow.hasEndArrow === true ||
        unifiedArrow.pointerAtEnd === true;
      type = hasStartArrow && hasEndArrow ? "DoubleSidedArrow" : "Arrow";
      // Set it on the target for future reference
      if (target?.attrs) {
        target.attrs.type = type;
      }
    } else if (type === "Arrow" && !target?.attrs?.type) {
      // Fallback: If type is "Arrow" from getClassName() but attrs.type is not set,
      // and we didn't find it in lines array, keep it as Arrow
      if (target?.attrs) {
        target.attrs.type = "Arrow";
      }
    }


    // CRITICAL: Early check - if a Text element with selectedContent is already selected,
    // and we're clicking within it (either directly on it or on its child elements like parts),
    // ensure selectedObject is set first, then skip the rest of canvas click handler
    // This is needed so that allowDbl() in part handlers can work correctly
    if (selectedObject?.id && selectedObject?.type === "Text") {
      const textWithContent =
        texts?.find((t) => t.id === selectedObject.id) ||
        offCanvasTexts?.find((t) => t.id === selectedObject.id);
      if (textWithContent?.selectedContent) {
        // Check if the click is on the text element itself or potentially within it
        // (target might be the Text element, or a Group/HTML element within it)
        // CRITICAL: targetId might match the text element ID even if target type is Group
        const isClickingOnTextElement =
          target?.attrs?.type === "Text" && targetId === selectedObject.id;
        // CRITICAL: Also check if targetId matches selectedObject.id (common case for Groups within text)
        const isTargetIdMatchingTextId = targetId === selectedObject.id;
        const isClickingWithinTextElement =
          isTargetIdMatchingTextId ||
          target?.attrs?.groupId === selectedObject.id ||
          target?.attrs?.dataGroupid === selectedObject.id ||
          (target?.getParent &&
            target.getParent()?.attrs?.id === selectedObject.id);

        if (isClickingOnTextElement || isClickingWithinTextElement) {

          // CRITICAL: Ensure selectedObject and related state are set
          // (They should already be set since we checked selectedObject.id, but ensure consistency)
          setShowTextBox(true);
          // Always ensure selectedTextId matches selectedObject.id
          setSelectedTextId(selectedObject.id);

          // Now return early - let the part's onClick/onDoubleClick handlers manage the rest
          return;
        }
      }
    }

    // Check if clicking on background: no target, stage, background Rect (id="bg"), or Transformer (selection box)
    // CRITICAL: Transformer is on top when multi-selecting — first click often hits it, so treat as background to clear in one click
    const isBackgroundClick =
      !targetId ||
      target.getClassName() === "Stage" ||
      targetId === "bg" ||
      target.getClassName() === "Transformer";

    if (isBackgroundClick) {
      // Main top-layer images can sometimes route click targets as Stage.
      // Before background clear logic, hit-test top layer and select image if present.
      if (topLayerStageRef?.current && e?.evt) {
        try {
          const topStage = topLayerStageRef.current;
          const topContainer = topStage.container?.();
          const rect = topContainer?.getBoundingClientRect?.();
          const clientX = e.evt.clientX;
          const clientY = e.evt.clientY;
          const withinTopStage =
            !!rect &&
            clientX >= rect.left &&
            clientX <= rect.right &&
            clientY >= rect.top &&
            clientY <= rect.bottom;
          if (withinTopStage) {
            if (typeof topStage.setPointersPositions === "function") {
              topStage.setPointersPositions(e.evt);
            }
            const stagePointer = topStage.getPointerPosition?.();
            const fallbackPointer = rect
              ? { x: clientX - rect.left, y: clientY - rect.top }
              : null;
            const hitPoint = stagePointer || fallbackPointer;
            const hitNode =
              hitPoint && typeof topStage.getIntersection === "function"
                ? topStage.getIntersection(hitPoint)
                : null;
            const hitId =
              hitNode?.attrs?.id ??
              (typeof hitNode?.id === "function" ? hitNode.id() : null);
            let hitImage =
              (hitId &&
                (images?.find((img) => img.id === hitId) ||
                  offCanvasImages?.find((img) => img.id === hitId))) ||
              null;

            let bboxNodeId = null;
            if (!hitImage && hitPoint) {
              const imageNodes = topStage.find?.("Image") || [];
              for (let i = imageNodes.length - 1; i >= 0; i -= 1) {
                const node = imageNodes[i];
                if (!node) continue;
                const nodeRect = node.getClientRect({ relativeTo: topStage });
                const withinNodeRect =
                  hitPoint.x >= nodeRect.x &&
                  hitPoint.x <= nodeRect.x + nodeRect.width &&
                  hitPoint.y >= nodeRect.y &&
                  hitPoint.y <= nodeRect.y + nodeRect.height;
                if (!withinNodeRect) continue;
                bboxNodeId =
                  node?.attrs?.id ??
                  (typeof node?.id === "function" ? node.id() : null);
                if (bboxNodeId) {
                  hitImage =
                    images?.find((img) => img.id === bboxNodeId) ||
                    offCanvasImages?.find((img) => img.id === bboxNodeId) ||
                    null;
                }
                if (hitImage) break;
              }
            }

            console.log("🟣 [CLICK-HANDLER][TOPLAYER-HITTEST]", {
              clientX,
              clientY,
              withinTopStage,
              stagePointer,
              fallbackPointer,
              hitPoint,
              hitNodeClass: hitNode?.getClassName?.(),
              hitNodeId: hitId,
              bboxNodeId,
              selectedId: hitImage?.id || null,
              topStageScale: {
                x: topStage.scaleX?.(),
                y: topStage.scaleY?.(),
              },
              topStagePos: {
                x: topStage.x?.(),
                y: topStage.y?.(),
              },
            });

            if (hitImage) {
              const normalizedHitSelection = {
                id: hitImage.id,
                name: hitImage.name || hitImage.id || "Unnamed",
                ...hitImage,
                type: "Image",
                // Keep transformer attach cache deterministic for top-layer stage selections.
                _canvas: hitImage.isOffCanvas ? "offCanvas" : "main",
              };
              console.log("🟣 [CLICK-HANDLER][TOPLAYER-SELECT]", {
                id: normalizedHitSelection.id,
                type: normalizedHitSelection.type,
                isOnTopLayer: normalizedHitSelection.isOnTopLayer,
                canvas: normalizedHitSelection._canvas,
              });
              setSelectedObjects([normalizedHitSelection]);
              setSelectedObject(normalizedHitSelection);
              setSelectedImage(normalizedHitSelection);
              setSelectedText(null);
              setShowTextBox(false);
              setSelectedTextId(null);
              e.evt?.preventDefault?.();
              e.evt?.stopPropagation?.();
              return;
            }
          }
        } catch (_err) {
          console.warn("🟣 [CLICK-HANDLER][TOPLAYER-HITTEST][ERROR]", _err);
        }
      }
      // Ignore the synthetic/background click that can fire right after
      // a selection box drag ends (to avoid immediate deselection)
      const liveSelectedObjects = selectedObjectsRef
        ? selectedObjectsRef.current
        : selectedObjects;

      // Preserve existing selection when user clicks background of the OTHER stage.
      // Example: main selected + click off-canvas background should not clear main selection.
      const inferElementCanvas = (el) => {
        if (!el?.id) return null;
        const id = el.id;
        const inMain =
          rectangles.some((r) => r.id === id) ||
          circles.some((c) => c.id === id) ||
          images.some((i) => i.id === id) ||
          videos.some((v) => v.id === id) ||
          texts.some((t) => t.id === id) ||
          arrows.some((a) => a.id === id) ||
          doubleSidedArrows.some((d) => d.id === id) ||
          lines.some((l) => l.id === id);
        const inOff =
          offCanvasRectangles.some((r) => r.id === id) ||
          offCanvasCircles.some((c) => c.id === id) ||
          offCanvasImages.some((i) => i.id === id) ||
          offCanvasVideos.some((v) => v.id === id) ||
          offCanvasTexts.some((t) => t.id === id) ||
          offCanvasArrows.some((a) => a.id === id) ||
          offCanvasDoubleSidedArrows.some((d) => d.id === id) ||
          offCanvasLines.some((l) => l.id === id);
        if (inMain && !inOff) return "main";
        if (inOff && !inMain) return "offCanvas";
        return null;
      };

      const inferSelectionCanvas = () => {
        const current =
          liveSelectedObjects && liveSelectedObjects.length > 0
            ? liveSelectedObjects
            : selectedObject
              ? [selectedObject]
              : [];
        if (current.length === 0) return null;
        const canvases = current
          .map(inferElementCanvas)
          .filter((canvas) => canvas !== null);
        if (canvases.length === 0) return null;
        const allMain = canvases.every((canvas) => canvas === "main");
        const allOff = canvases.every((canvas) => canvas === "offCanvas");
        if (allMain) return "main";
        if (allOff) return "offCanvas";
        return null;
      };

      const inferClickCanvas = () => {
        const clickStage =
          typeof target?.getStage === "function" ? target.getStage() : null;
        if (offCanvasStageRef?.current && clickStage === offCanvasStageRef.current) {
          return "offCanvas";
        }
        if (stageRef?.current && clickStage === stageRef.current) {
          return "main";
        }

        // Fallback using DOM hit testing.
        const mouseX =
          window.lastMouseX !== undefined ? window.lastMouseX : e?.evt?.clientX;
        const mouseY =
          window.lastMouseY !== undefined ? window.lastMouseY : e?.evt?.clientY;
        const node =
          mouseX != null && mouseY != null
            ? document.elementFromPoint(mouseX, mouseY)
            : null;
        const offContainer = offCanvasStageRef?.current?.container?.();
        const mainContainer = stageRef?.current?.container?.();
        if (offContainer && node && offContainer.contains(node)) return "offCanvas";
        if (mainContainer && node && mainContainer.contains(node)) return "main";
        return null;
      };
      
      // CRITICAL: Use pre-mouse-down captured selection count instead of current selection count
      // Because Stage.onMouseDown already cleared the selection, so liveSelectedObjects is empty now
      const originalSelectionCount = preMouseDownSelectionCountRef?.current || 0;
      const hasGroupSelection = originalSelectionCount > 1;
      
      // DEBUG: Log selection state in clickHandler
      console.log("🟠 [CLICK-HANDLER] Selection state check", {
        selectedObjectsLength: selectedObjects?.length || 0,
        selectedObjectsRefCurrent: selectedObjectsRef?.current,
        selectedObjectsRefLength: selectedObjectsRef?.current?.length || 0,
        liveSelectedObjectsLength: liveSelectedObjects?.length || 0,
        originalSelectionCount,
        hasGroupSelection,
        targetId,
        targetType: target?.getClassName?.(),
      });

      const selectedCanvasType = inferSelectionCanvas();
      const clickedCanvasType = inferClickCanvas();
      if (
        selectedCanvasType &&
        clickedCanvasType &&
        selectedCanvasType !== clickedCanvasType
      ) {
        console.log("🟠 [CLICK-HANDLER] Preserving cross-stage selection", {
          selectedCanvasType,
          clickedCanvasType,
        });
        return;
      }

      // Ambiguous Stage background click (targetId undefined) can occur when clicking the
      // other canvas stage; in that case keep current selection instead of clearing.
      if (
        selectedCanvasType &&
        !clickedCanvasType &&
        target?.getClassName?.() === "Stage" &&
        !targetId &&
        (liveSelectedObjects?.length || 0) > 0
      ) {
        console.log("🟠 [CLICK-HANDLER] Preserving selection on ambiguous stage click", {
          selectedCanvasType,
          clickedCanvasType,
          targetType: target?.getClassName?.(),
          liveSelectedCount: liveSelectedObjects?.length || 0,
        });
        return;
      }
      
      if (hasGroupSelection) {
        // Keep active group selection on background/stage clicks.
        console.log("🟠 [CLICK-HANDLER] Preserving group selection, returning early");
        return;
      }
      // Do not call endElementEditing here; selection effect in MyCanvas runs when selectedObject becomes null.
      // Clear all selections when clicking on background
      // But don't clear if a tool is locked (clone mode uses lockedAction) or clone mode is active
      // Don't clear selectedObjects if lockedAction is set or clone mode is active (keeps editor open)
      // CRITICAL: Also don't clear if multi-select mode is active (prevents clearing selections when multi-select is enabled)
      // HOWEVER: If the user clicked on whitespace (isBackgroundClick), they might WANT to clear selections
      // The previous logic prevented clearing if isMultiSelectMode is true.
      // But we should allow clearing if it's a genuine background click, and we should also disable multi-select mode in that case.
      const shouldClearBackground = !lockedAction && !isCloneMode;

      if (shouldClearBackground) {
        console.warn("🔵 [CLICK-HANDLER] clearing background selection", {
          targetId,
          targetType: target?.getClassName?.(),
          liveLen: liveSelectedObjects?.length || 0,
          selectedObjectId: selectedObject?.id,
          stack: new Error().stack,
        });

        // If we are in multi-select mode, we should disable it when clearing all selections
        const currentIsMultiSelectMode = isMultiSelectModeRef?.current ?? isMultiSelectMode;
        if (currentIsMultiSelectMode) {
          if (setIsMultiSelectMode) {
            setIsMultiSelectMode(false);
          } else {
            console.warn("🔵 [CLICK-HANDLER] setIsMultiSelectMode not provided, cannot disable multi-select mode");
          }
        }

        setSelectedObjects([]);
        setSelectedObject(null);
        setSelectedText(null); // Clear selectedText too
        setShowTextBox(false);
        setSelectedTextId(null);
      } else {
      }
      setTexts((prevTexts) =>
        prevTexts.map((t) => ({
          ...t,
          selectedContent: null,
        }))
      );
      return;
    }

    // Allow selection of objects when clicking on them
    // The restriction below was preventing objects from being selected
    // const hasExistingSelection = selectedObjects.length > 0 || selectedObject;

    // if (!hasExistingSelection) {
    //   // Don't allow selection without clicking background first
    //   return;
    // }

    if (target.attrs.type !== "Text") {
      setSelectedText(null);
      setShowTextBox(false);
      setSelectedTextId(null);
    } else {
      setShowTextBox(true);
    }

    // Don't reset action if a tool is locked
    if (
      !isDrawingArrow &&
      !isDrawingDoubleArrow &&
      !isDrawingLine &&
      !lockedAction
    ) {
      setAction(ACTIONS_SELECT);
    }

    // -----------------------------
    // 🎯 Selection Logic
    // -----------------------------
    if (action === ACTIONS_SELECT) {
      if (!targetId) {
        const liveSelectedObjects =
          selectedObjectsRef?.current && Array.isArray(selectedObjectsRef.current)
            ? selectedObjectsRef.current
            : selectedObjects;
        const hasGroupSelection = (liveSelectedObjects?.length || 0) > 1;
        if (hasGroupSelection) {
          return;
        }
        if (!e.evt.shiftKey) {
          // End element editing before clearing selections
          // But don't end editing if clone mode is active (lockedAction) - keeps editor open for rapid cloning
          // Do not call endElementEditing here; selection effect handles it when selectedObject becomes null.
          // CRITICAL: Don't clear selectedObjects if multi-select mode is active (prevents clearing selections when multi-select is enabled)
          const currentIsMultiSelectMode = isMultiSelectModeRef?.current ?? isMultiSelectMode;
          if (!currentIsMultiSelectMode) {
            console.warn("🔵 [CLICK-HANDLER] clearing stage selection (no targetId)", {
              liveLen: liveSelectedObjects?.length || 0,
              selectedObjectId: selectedObject?.id,
              stack: new Error().stack,
            });
            setSelectedObjects([]);
          }
          // Don't clear selectedObject if clone mode is active (keeps editor open)
          // Check both lockedAction and isCloneMode
          if (!lockedAction && !isCloneMode) {
            setSelectedObject(null);
            setSelectedText(null); // Clear selectedText too
            setShowTextBox(false);
          }
        }
        return;
      }

      // Helper function to check if an element is in main canvas arrays
      // This uses the element ID and type to check against the actual state arrays
      // True only when the element id is in *main* canvas state (not off-canvas-only).
      // Used for cross-canvas multi-select rules; off-canvas-only shapes must not be treated as "main".
      const isElementInMainCanvas = (elementId, elementType) => {
        if (!elementId || !elementType) {
          return false;
        }

        switch (elementType) {
          case "Rect":
            return rectangles.some((r) => r.id === elementId);
          case "Circle":
            return circles.some((c) => c.id === elementId);
          case "Image":
            return images.some((i) => i.id === elementId);
          case "Video":
            return videos.some((v) => v.id === elementId);
          case "Text":
            return texts.some((t) => t.id === elementId);
          case "Arrow": {
            if (arrows.some((a) => a.id === elementId)) return true;
            return lines.some((l) => {
              if (l.id !== elementId) return false;
              const hasStartArrow =
                l.hasStartArrow === true || l.pointerAtBeginning === true;
              const hasEndArrow =
                l.hasEndArrow === true || l.pointerAtEnd === true;
              return hasStartArrow || hasEndArrow;
            });
          }
          case "Line":
            return lines.some((l) => {
              if (l.id !== elementId) return false;
              return (
                !l.hasStartArrow &&
                !l.hasEndArrow &&
                !l.pointerAtBeginning &&
                !l.pointerAtEnd
              );
            });
          case "DoubleSidedArrow": {
            if (doubleSidedArrows.some((d) => d.id === elementId)) return true;
            return lines.some((l) => {
              if (l.id !== elementId) return false;
              const hasStartArrow =
                l.hasStartArrow === true || l.pointerAtBeginning === true;
              const hasEndArrow =
                l.hasEndArrow === true || l.pointerAtEnd === true;
              return hasStartArrow && hasEndArrow;
            });
          }
          default:
            return false;
        }
      };

      const isElementInOffCanvas = (elementId, elementType) => {
        if (!elementId || !elementType) {
          return false;
        }

        switch (elementType) {
          case "Rect":
            return offCanvasRectangles.some((r) => r.id === elementId);
          case "Circle":
            return offCanvasCircles.some((c) => c.id === elementId);
          case "Image":
            return offCanvasImages.some((i) => i.id === elementId);
          case "Video":
            return offCanvasVideos.some((v) => v.id === elementId);
          case "Text":
            return offCanvasTexts.some((t) => t.id === elementId);
          case "Arrow": {
            if (offCanvasArrows.some((a) => a.id === elementId)) return true;
            return offCanvasLines.some((l) => {
              if (l.id !== elementId) return false;
              const hasStartArrow =
                l.hasStartArrow === true || l.pointerAtBeginning === true;
              const hasEndArrow =
                l.hasEndArrow === true || l.pointerAtEnd === true;
              return hasStartArrow || hasEndArrow;
            });
          }
          case "Line":
            return offCanvasLines.some((l) => {
              if (l.id !== elementId) return false;
              return (
                !l.hasStartArrow &&
                !l.hasEndArrow &&
                !l.pointerAtBeginning &&
                !l.pointerAtEnd
              );
            });
          case "DoubleSidedArrow": {
            if (offCanvasDoubleSidedArrows.some((d) => d.id === elementId))
              return true;
            return offCanvasLines.some((l) => {
              if (l.id !== elementId) return false;
              const hasStartArrow =
                l.hasStartArrow === true || l.pointerAtBeginning === true;
              const hasEndArrow =
                l.hasEndArrow === true || l.pointerAtEnd === true;
              return hasStartArrow && hasEndArrow;
            });
          }
          default:
            return false;
        }
      };

      // Get the actual element from state to ensure we have the correct maintainAspectRatio
      // Check both main canvas and off-canvas states
      let actualElement = null;
      if (isShapeType(type, "Rect")) {
        actualElement =
          rectangles.find((rect) => rect.id === targetId) ||
          offCanvasRectangles.find((rect) => rect.id === targetId);
      } else if (isShapeType(type, "Circle")) {
        actualElement =
          circles.find((circle) => circle.id === targetId) ||
          offCanvasCircles.find((circle) => circle.id === targetId);
      } else if (isShapeType(type, "Image")) {
        actualElement =
          images.find((img) => img.id === targetId) ||
          offCanvasImages.find((img) => img.id === targetId);
      } else if (isShapeType(type, "Video")) {
        actualElement =
          videos.find((vid) => vid.id === targetId) ||
          offCanvasVideos.find((vid) => vid.id === targetId);
      } else if (isShapeType(type, "Arrow")) {
        // Check arrows array first
        actualElement =
          arrows.find((arrow) => arrow.id === targetId) ||
          offCanvasArrows.find((arrow) => arrow.id === targetId);
        // If not found, check lines array for unified arrow elements (with arrow heads)
        if (!actualElement) {
          actualElement =
            lines.find((line) => {
              if (line.id !== targetId) return false;
              const hasStartArrow =
                line.hasStartArrow === true || line.pointerAtBeginning === true;
              const hasEndArrow =
                line.hasEndArrow === true || line.pointerAtEnd === true;
              return hasStartArrow || hasEndArrow;
            }) ||
            offCanvasLines.find((line) => {
              if (line.id !== targetId) return false;
              const hasStartArrow =
                line.hasStartArrow === true || line.pointerAtBeginning === true;
              const hasEndArrow =
                line.hasEndArrow === true || line.pointerAtEnd === true;
              return hasStartArrow || hasEndArrow;
            });
        }
      } else if (isShapeType(type, "Line")) {
        // Lines without arrow heads are in the lines array
        actualElement =
          lines.find((line) => {
            if (line.id !== targetId) return false;
            // Only return lines without arrow heads
            return (
              !line.hasStartArrow &&
              !line.hasEndArrow &&
              !line.pointerAtBeginning &&
              !line.pointerAtEnd
            );
          }) ||
          offCanvasLines.find((line) => {
            if (line.id !== targetId) return false;
            // Only return lines without arrow heads
            return (
              !line.hasStartArrow &&
              !line.hasEndArrow &&
              !line.pointerAtBeginning &&
              !line.pointerAtEnd
            );
          });
      } else if (isShapeType(type, "DoubleSidedArrow")) {
        // Check doubleSidedArrows array first
        actualElement =
          doubleSidedArrows.find((arrow) => arrow.id === targetId) ||
          offCanvasDoubleSidedArrows.find((arrow) => arrow.id === targetId);
        // If not found, check lines array for unified double-sided arrow elements
        if (!actualElement) {
          actualElement =
            lines.find((line) => {
              if (line.id !== targetId) return false;
              const hasStartArrow =
                line.hasStartArrow === true || line.pointerAtBeginning === true;
              const hasEndArrow =
                line.hasEndArrow === true || line.pointerAtEnd === true;
              return hasStartArrow && hasEndArrow;
            }) ||
            offCanvasLines.find((line) => {
              if (line.id !== targetId) return false;
              const hasStartArrow =
                line.hasStartArrow === true || line.pointerAtBeginning === true;
              const hasEndArrow =
                line.hasEndArrow === true || line.pointerAtEnd === true;
              return hasStartArrow && hasEndArrow;
            });
        }
      } else if (isShapeType(type, "Text")) {
        // Text elements are rendered as Groups, so check attrs.type
        actualElement =
          texts.find((text) => text.id === targetId) ||
          offCanvasTexts.find((text) => text.id === targetId);
      }

      // 🎯 NEW: Check if clicking on transparent shape and find image underneath
      let finalTarget = target;
      let finalTargetId = targetId;
      let finalType = type;
      let finalActualElement = actualElement;

      // Check if this was a stroke click - if so, don't override with image selection
      const isStrokeClick = target?.attrs?.isStrokeClick === true;


      if (isStrokeClick) {
        // Don't process transparent shape logic for stroke clicks
        // The shape's onClick handler has already handled it correctly
      } else {
        // Check if the clicked element is a transparent rectangle or circle
        if (
          (isShapeType(type, "Rect") || isShapeType(type, "Circle")) &&
          actualElement
        ) {
          const fillOpacity = actualElement.fillOpacity ?? 1;
          const isTransparent = fillOpacity === 0 || fillOpacity < 0.1;

          if (isTransparent) {
            // Calculate shape bounds once outside the filter
            let shapeLeft, shapeRight, shapeTop, shapeBottom;

            if (isShapeType(type, "Circle")) {
              // For circles, use radius-based bounds
              const radius = actualElement.radius || actualElement.width / 2;
              shapeLeft = actualElement.x - radius;
              shapeRight = actualElement.x + radius;
              shapeTop = actualElement.y - radius;
              shapeBottom = actualElement.y + radius;
            } else {
              // For rectangles
              shapeLeft = actualElement.x;
              shapeRight = actualElement.x + actualElement.width;
              shapeTop = actualElement.y;
              shapeBottom = actualElement.y + actualElement.height;
            }

            // Find images that are underneath this transparent shape
            const imagesUnderneath = images.filter((img) => {
              // Check if the click point is within the image bounds
              const imgLeft = img.x;
              const imgRight = img.x + img.width;
              const imgTop = img.y;
              const imgBottom = img.y + img.height;

              const isWithinImageBounds =
                x >= imgLeft && x <= imgRight && y >= imgTop && y <= imgBottom;

              // Check for overlap between image and shape
              const hasOverlap = !(
                imgRight < shapeLeft ||
                imgLeft > shapeRight ||
                imgBottom < shapeTop ||
                imgTop > shapeBottom
              );

              return isWithinImageBounds && hasOverlap;
            });

            // If we found an image underneath, select it instead
            if (imagesUnderneath.length > 0) {
              // If multiple images are found, prioritize the one with the largest overlap area
              let imageToSelect = imagesUnderneath[0];

              if (imagesUnderneath.length > 1) {
                // Find the image with the largest overlap area
                let maxOverlapArea = 0;
                for (const img of imagesUnderneath) {
                  const imgLeft = img.x;
                  const imgRight = img.x + img.width;
                  const imgTop = img.y;
                  const imgBottom = img.y + img.height;

                  const overlapLeft = Math.max(imgLeft, shapeLeft);
                  const overlapRight = Math.min(imgRight, shapeRight);
                  const overlapTop = Math.max(imgTop, shapeTop);
                  const overlapBottom = Math.min(imgBottom, shapeBottom);

                  const overlapArea =
                    Math.max(0, overlapRight - overlapLeft) *
                    Math.max(0, overlapBottom - overlapTop);

                  if (overlapArea > maxOverlapArea) {
                    maxOverlapArea = overlapArea;
                    imageToSelect = img;
                  }
                }
              }

              finalTarget = { attrs: { ...imageToSelect, type: "Image" } };
              finalTargetId = imageToSelect.id;
              finalType = "Image";
              finalActualElement = imageToSelect;
            }
          }
        }
      }

      // 🚫 CRITICAL: Check cross-canvas selection AFTER transparent shape logic
      // This must happen BEFORE creating newSelected and BEFORE any selection logic

      // Build a comprehensive list of all currently selected elements
      const allSelectedElements = [];

      // Add all elements from selectedObjects array
      if (selectedObjects && selectedObjects.length > 0) {
        selectedObjects.forEach((obj) => {
          if (obj && obj.id && obj.type) {
            allSelectedElements.push(obj);
          }
        });
      }

      // Also include selectedObject if it's not already in the list
      if (selectedObject && selectedObject.id && selectedObject.type) {
        const alreadyIncluded = allSelectedElements.some(
          (obj) => obj.id === selectedObject.id
        );
        if (!alreadyIncluded) {
          allSelectedElements.push(selectedObject);
        }
      }


      // If we have ANY selected elements, check for cross-canvas conflict
      if (allSelectedElements.length > 0) {
        const allFromMainCanvas = allSelectedElements.every((obj) =>
          isElementInMainCanvas(obj.id, obj.type),
        );
        const allFromOffCanvas = allSelectedElements.every((obj) =>
          isElementInOffCanvas(obj.id, obj.type),
        );

        const clickedElementIsMainCanvas = isElementInMainCanvas(
          finalTargetId,
          finalType,
        );
        const clickedElementIsOffCanvas = isElementInOffCanvas(
          finalTargetId,
          finalType,
        );

        const crossCanvasMismatch =
          (allFromMainCanvas && clickedElementIsOffCanvas) ||
          (allFromOffCanvas && clickedElementIsMainCanvas);

        // Normal mode: clear and select new; multi-select: block other stage (no mixing).
        if (crossCanvasMismatch) {
          const currentIsMultiSelectMode = isMultiSelectModeRef?.current ?? isMultiSelectMode;
          if (currentIsMultiSelectMode) {
            e.evt?.stopPropagation?.();
            e.evt?.preventDefault?.();
            return;
          }
          // Normal mode: clear previous selections and continue with new selection
          setSelectedObject(null);
          setSelectedObjects([]);
          setShowTextBox(false);
          setSelectedText(null);
          if (setSelectedArrow) {
            setSelectedArrow(null);
          }
          if (setSelectedDoubleSidedArrow) {
            setSelectedDoubleSidedArrow(null);
          }
          if (setSelectedLine) {
            setSelectedLine(null);
          }
          // Continue with the new selection (don't return early)
        }

      } else {
      }

      // CRITICAL: For unified line/arrow elements, determine type from hasStartArrow/hasEndArrow if type is missing
      let determinedType = finalType || finalTarget.attrs.type || type;

      // If type is still undefined and this is a line/arrow element, try to determine it from the actual element
      if (!determinedType && finalActualElement) {
        const hasStartArrow = finalActualElement.hasStartArrow === true || finalActualElement.pointerAtBeginning === true;
        const hasEndArrow = finalActualElement.hasEndArrow === true || finalActualElement.pointerAtEnd === true;

        if (hasStartArrow && hasEndArrow) {
          determinedType = "DoubleSidedArrow";
        } else if (hasStartArrow || hasEndArrow) {
          determinedType = "Arrow";
        } else {
          determinedType = "Line";
        }
      }

      // CRITICAL: Ensure type is preserved - set it after spreading attrs to prevent undefined override
      const newSelected = {
        id: finalTargetId,
        name: finalTarget.attrs.name || finalTarget.attrs.id || "Unnamed",
        ...finalTarget.attrs,
        // Override with actual state properties if available
        ...(finalActualElement && {
          maintainAspectRatio: finalActualElement.maintainAspectRatio,
          ...finalActualElement,
        }),
        // CRITICAL: Set type last to ensure it's not overridden by undefined from attrs
        type: determinedType,
      };


      let nextSelection = [];

      const selectionIdKey = (id) =>
        id === undefined || id === null ? "" : String(id);

      const buildMergedSelectionArray = () => {
        const arr = [];
        const ids = new Set();
        if (selectedObjects && selectedObjects.length > 0) {
          selectedObjects.forEach((obj) => {
            if (obj?.id == null) return;
            const key = selectionIdKey(obj.id);
            if (!ids.has(key)) {
              arr.push(obj);
              ids.add(key);
            }
          });
        }
        if (selectedObject?.id != null) {
          const key = selectionIdKey(selectedObject.id);
          if (!ids.has(key)) {
            arr.push(selectedObject);
            ids.add(key);
          }
        }
        return arr;
      };

      const removeIdFromMultiSelection = (mergedArray, removeId) => {
        const removeKey = selectionIdKey(removeId);
        const nextSel = mergedArray.filter(
          (o) => o != null && selectionIdKey(o.id) !== removeKey,
        );
        if (nextSel.length === 0) {
          setSelectedObjects([]);
          setSelectedObject(null);
          setSelectedText(null);
          setShowTextBox(false);
          setSelectedTextId(null);
          return;
        }
        setSelectedObjects(nextSel);
        const newPrimary = nextSel[0];
        setSelectedObject(newPrimary);
        if (newPrimary && isShapeType(newPrimary.type, "Text")) {
          setTexts((prevTexts) => {
            const tw =
              prevTexts.find(
                (t) => selectionIdKey(t.id) === selectionIdKey(newPrimary.id),
              ) ||
              offCanvasTexts.find(
                (t) => selectionIdKey(t.id) === selectionIdKey(newPrimary.id),
              );
            setSelectedText({
              ...newPrimary,
              ...(tw || {}),
              type: "Text",
              selectedContent: tw?.selectedContent ?? null,
            });
            return prevTexts;
          });
          setShowTextBox(true);
          setSelectedTextId(newPrimary.id);
        } else {
          setSelectedText(null);
          setShowTextBox(false);
          setSelectedTextId(null);
        }
      };

      if (e.evt.shiftKey) {
        const clickKey = selectionIdKey(finalTargetId);
        const exists = selectedObjects.find(
          (obj) => selectionIdKey(obj?.id) === clickKey,
        );
        if (exists) {
          // Removing from selection - allow this
          nextSelection = selectedObjects.filter(
            (obj) => selectionIdKey(obj?.id) !== clickKey,
          );
          setSelectedObject(null);

          if (isShapeType(finalTarget.attrs.type, "Text")) {
            setSelectedText(null);
            setShowTextBox(false);
          }
        } else {
          // Adding to selection - check cross-canvas restriction
          if (selectedObjects && selectedObjects.length > 0) {
            // Build list of all selected elements (including selectedObject if it exists)
            const allSelectedElements = [...selectedObjects];
            if (
              selectedObject &&
              !allSelectedElements.some((obj) => obj.id === selectedObject.id)
            ) {
              allSelectedElements.push(selectedObject);
            }

            const allFromMainCanvas = allSelectedElements.every((obj) =>
              isElementInMainCanvas(obj.id, obj.type),
            );
            const allFromOffCanvas = allSelectedElements.every((obj) =>
              isElementInOffCanvas(obj.id, obj.type),
            );
            const clickedMain = isElementInMainCanvas(finalTargetId, finalType);
            const clickedOff = isElementInOffCanvas(finalTargetId, finalType);
            if (
              (allFromMainCanvas && clickedOff) ||
              (allFromOffCanvas && clickedMain)
            ) {
              e.evt?.stopPropagation?.();
              e.evt?.preventDefault?.();
              return;
            }
          }

          // Safe to add - same canvas type
          nextSelection = [...selectedObjects, newSelected];
          // Do not spread prev into newSelected — that overwrites id/type and corrupts multi-select state
          setSelectedObject({
            ...newSelected,
            fontSize: finalTarget.attrs.fontSize,
          });

          if (isShapeType(finalTarget.attrs.type, "Text")) {
            // CRITICAL: Read selectedContent from texts array to preserve it
            setTexts((prevTexts) => {
              const textWithContent =
                prevTexts.find((t) => t.id === finalTargetId) ||
                offCanvasTexts.find((t) => t.id === finalTargetId);
              const preservedSelectedContent = textWithContent?.selectedContent;

              // Update selectedText with preserved selectedContent
              setSelectedText((prev) => ({
                ...prev,
                ...newSelected,
                fontSize: finalTarget.attrs.fontSize,
                // CRITICAL: Preserve selectedContent from texts array
                selectedContent:
                  preservedSelectedContent || prev?.selectedContent,
              }));

              return prevTexts; // Don't modify texts array, just read from it
            });
            setShowTextBox(true);
          }
        }
      } else {
        // Check if clicking on the same element that's already selected
        const isSameElement =
          selectedObject && selectedObject.id === finalTargetId;

        // When multi-select mode is enabled, skip the normal clearing behavior and add to selection instead
        // This must be checked FIRST before any clearing logic
        const currentIsMultiSelectMode = isMultiSelectModeRef?.current ?? isMultiSelectMode;
        if (currentIsMultiSelectMode && !isSameElement) {
          // Build current selection - MUST include selectedObject if it exists, plus any items from selectedObjects
          // This ensures we preserve the currently selected element when adding new ones
          const currentSelectionArray = [];
          const currentSelectionSet = new Set();

          // First, add all items from selectedObjects (string keys so id types stay consistent)
          if (selectedObjects && selectedObjects.length > 0) {
            selectedObjects.forEach((obj) => {
              if (obj?.id == null) return;
              const key = selectionIdKey(obj.id);
              if (!currentSelectionSet.has(key)) {
                currentSelectionArray.push(obj);
                currentSelectionSet.add(key);
              }
            });
          }

          // Then, ensure selectedObject is included (it might not be in selectedObjects yet)
          // This is CRITICAL: when you first select a rectangle, selectedObject is set but selectedObjects might be empty
          if (selectedObject?.id != null) {
            const key = selectionIdKey(selectedObject.id);
            if (!currentSelectionSet.has(key)) {
              currentSelectionArray.push(selectedObject);
              currentSelectionSet.add(key);
            }
          }

          // Check if clicking on an element from the same canvas type
          // Check ALL elements in currentSelectionArray to ensure they're all from the same canvas

          if (currentSelectionArray.length > 0) {
            const allFromMainCanvas = currentSelectionArray.every((obj) =>
              isElementInMainCanvas(obj.id, obj.type),
            );
            const allFromOffCanvas = currentSelectionArray.every((obj) =>
              isElementInOffCanvas(obj.id, obj.type),
            );

            const clickedMain = isElementInMainCanvas(finalTargetId, finalType);
            const clickedOff = isElementInOffCanvas(finalTargetId, finalType);
            if (
              (allFromMainCanvas && clickedOff) ||
              (allFromOffCanvas && clickedMain)
            ) {
              e.evt?.stopPropagation?.();
              e.evt?.preventDefault?.();
              return;
            }

            // Toggle off when clicking an element that's already in the selection (multi-select mode, no Shift)
            if (currentSelectionSet.has(selectionIdKey(finalTargetId))) {
              removeIdFromMultiSelection(currentSelectionArray, finalTargetId);
              return;
            }

            // Add the new element to the selection
            nextSelection = [...currentSelectionArray, newSelected];

            // Last-clicked element becomes primary; never merge prev (would corrupt id/type)
            setSelectedObject({
              ...newSelected,
              fontSize: finalTarget.attrs.fontSize,
            });

            if (isShapeType(finalTarget.attrs.type, "Text")) {
              setTexts((prevTexts) => {
                const textWithContent =
                  prevTexts.find((t) => t.id === finalTargetId) ||
                  offCanvasTexts.find((t) => t.id === finalTargetId);
                const preservedSelectedContent =
                  textWithContent?.selectedContent;

                setSelectedText((prev) => ({
                  ...prev,
                  ...newSelected,
                  fontSize: finalTarget.attrs.fontSize,
                  selectedContent:
                    preservedSelectedContent || prev?.selectedContent,
                }));

                return prevTexts;
              });
              setShowTextBox(true);
            }

            // CRITICAL: Use functional update and ensure selectedObject is included when prev is empty
            // (e.g. TextElement may have called setSelectedObjects([]) before delegating to this handler)
            // CRITICAL: Use flushSync to ensure state updates immediately so wrapper renders on first click
            flushSync(() => {
              setSelectedObjects((prev) => {
                const current = prev && Array.isArray(prev) ? [...prev] : [];
                const ids = new Set(
                  current.map((o) => (o?.id != null ? selectionIdKey(o.id) : "")),
                );
                if (selectedObject?.id != null) {
                  const sk = selectionIdKey(selectedObject.id);
                  if (!ids.has(sk)) {
                    current.push({
                      ...selectedObject,
                      type: selectedObject.type || "Text",
                      fontSize: selectedObject.fontSize,
                    });
                    ids.add(sk);
                  }
                }
                if (!ids.has(selectionIdKey(finalTargetId))) current.push(newSelected);
               
                return current;
              });
            });
            if (multiSelectCanvasClickDedupeRef && e?.evt) {
              multiSelectCanvasClickDedupeRef.current = {
                timeStamp: e.evt.timeStamp,
                targetId: finalTargetId,
              };
            }
            return;
          } else {
            // No existing selection - this is the first element being selected in multi-select mode
            // Just select this element normally

            setSelectedObject({
              ...newSelected,
              fontSize: finalTarget.attrs.fontSize,
            });

            if (isShapeType(finalTarget.attrs.type, "Text")) {
              setTexts((prevTexts) => {
                const textWithContent =
                  prevTexts.find((t) => t.id === finalTargetId) ||
                  offCanvasTexts.find((t) => t.id === finalTargetId);
                const preservedSelectedContent =
                  textWithContent?.selectedContent;

                setSelectedText((prev) => ({
                  ...prev,
                  ...newSelected,
                  fontSize: finalTarget.attrs.fontSize,
                  selectedContent:
                    preservedSelectedContent || prev?.selectedContent,
                }));

                return prevTexts;
              });
              setShowTextBox(true);
            }

            // Set selectedObjects to contain just this element
            setSelectedObjects([newSelected]);
            return;
          }
        }

        // CRITICAL: If clicking on a Text element that has selectedContent,
        // don't let the canvas click handler interfere - let the text element's handlers manage it
        // This prevents the canvas click handler from interfering with double-clicks on parts
        if (isShapeType(finalTarget.attrs.type, "Text") && isSameElement) {
          // Check if the text has selectedContent - if so, skip canvas click handler
          // Let the part's double-click handler manage the state
          const textWithContent =
            texts?.find((t) => t.id === finalTargetId) ||
            offCanvasTexts?.find((t) => t.id === finalTargetId);
          if (textWithContent?.selectedContent) {
            // Return early to let part handlers manage the interaction (double-click)
            return;
          }
        }

        if (currentIsMultiSelectMode && isSameElement) {
          const merged = buildMergedSelectionArray();
          if (
            merged.some((o) => o.id === finalTargetId) &&
            merged.length > 1
          ) {
            const d = multiSelectCanvasClickDedupeRef?.current;
            if (
              d &&
              e?.evt &&
              d.timeStamp === e.evt.timeStamp &&
              d.targetId === finalTargetId
            ) {
              multiSelectCanvasClickDedupeRef.current = null;
              return;
            }
            removeIdFromMultiSelection(merged, finalTargetId);
            return;
          }
        }

        if (isSameElement) {
          // Don't change selection if clicking on the same element
          return;
        }

        // Normal selection (not multi-select mode) - replace selection with new element
        // CRITICAL: If multi-select mode is enabled, we should have already handled it above
        // But add a safeguard check here to prevent clearing selection in multi-select mode
        if (currentIsMultiSelectMode) {
          // Multi-select mode is enabled but we didn't enter the multi-select branch above
          // This shouldn't happen, but if it does, don't clear the selection
          console.warn(
            "⚠️ [CANVAS-CLICK] Multi-select mode enabled but not handled in multi-select branch"
          );
          return;
        }

        nextSelection = [newSelected];
        setSelectedObject((prev) => ({
          ...prev,
          ...newSelected,
          fontSize: finalTarget.attrs.fontSize,
        }));

        if (isShapeType(finalTarget.attrs.type, "Text")) {
          // CRITICAL: Read selectedContent from texts array to preserve it
          // The canvas click handler should not clear selectedContent - let text element handlers manage it
          setTexts((prevTexts) => {
            const textWithContent =
              prevTexts.find((t) => t.id === finalTargetId) ||
              offCanvasTexts.find((t) => t.id === finalTargetId);
            const preservedSelectedContent = textWithContent?.selectedContent;

            // Update selectedText with preserved selectedContent
            setSelectedText((prev) => ({
              ...prev,
              ...newSelected,
              fontSize: finalTarget.attrs.fontSize,
              // CRITICAL: Preserve selectedContent from texts array
              selectedContent:
                preservedSelectedContent || prev?.selectedContent,
            }));

            return prevTexts; // Don't modify texts array, just read from it
          });
          setShowTextBox(true);
        }
      }

      setSelectedObjects(nextSelection);
    }
  };
};
